import React, { forwardRef, useRef, useState } from "react";
import html2pdf from "html2pdf.js";
import { Box, Grid, List, ListItem, Stack } from "@mui/material";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import { PhysicalOnePagerResultStyle } from "./PhysicalOnePagerResultStyle";
import VSNTypography from "../../../shared/VSNTypography";
import xray1 from "../../../assets/assesments/physical/xray/xray-1.svg";
import xray2 from "../../../assets/assesments/physical/xray/xray-2.svg";
import xray3 from "../../../assets/assesments/physical/xray/xray-3.svg";
import xray4 from "../../../assets/assesments/physical/xray/xray-4.svg";
import VSNLoader from "../../../shared/VSNLoader";
import VSNBreadcrumbs from "../../../shared/VSNBreadcrumbs";
import SwiperCardComponent from "../../assessment/SwiperCardComponent";
import {
  BulletIcon,
  ComparisonIcon,
  HistoryIcon,
  PurpalFlowerBullet,
  StarIcon,
} from "../../../shared/icon";
import VSNButton from "../../../shared/VSNButton";
import VSNImage from "../../../shared/VSNImage";
import MRFResultContainer from "../../../container/MRF.container";
import {
  PDFCommonFAQSection,
  PDFCommonSummarySection,
  PDFHeader,
  PDFRecommendationSection,
} from "./PhysicalDashboardPDFCommonComponent";

const swiperData = [
  {
    image: xray1,
  },
  {
    image: xray2,
  },
  {
    image: xray3,
  },
  {
    image: xray4,
  },
];

const PrintComponent = forwardRef((props, ref) => {
  const { result, header } = props;

  const classes = PhysicalOnePagerResultStyle();

  return (
    <Box sx={{ visibility: "hidden", height: 0, overflow: "hidden" }}>
      <Box ref={ref}>
        <Stack gap={2}>
          <Stack
            gap={2}
            sx={{
              pageBreakAfter: "always",
            }}
          >
            <PDFHeader title="Your Chest X-Ray, Explained" header={header} />

            <PDFCommonSummarySection
              title={result?.clinical_summary_of_your_x_ray_card?.title}
              description={
                result?.clinical_summary_of_your_x_ray_card?.description
              }
              data={result?.clinical_summary_of_your_x_ray_card?.content}
            />

            <PDFCommonFAQSection
              title={result?.chest_x_ray_card_faqs?.title}
              description={result?.chest_x_ray_card_faqs?.description}
              data={result?.chest_x_ray_card_faqs?.questions}
            />
          </Stack>

          <Stack
            gap={2}
            className={classes.assessmentReportCard}
            sx={{
              pageBreakAfter: "always",
              background: "#FAF6F0 !important",
            }}
          >
            <VSNTypography
              variant="h3"
              component="h3"
              className={classes.mdHeadingprimary}
              mb={1}
              sx={{
                fontSize: "16px !important",
                color: "#B29263 !important",
                "&:before": { background: "#B29263 !important" },
              }}
            >
              {result?.doctor_take_key_insights_from_your_x_ray_card?.title ??
                ""}
            </VSNTypography>

            <Stack
              direction="column"
              justifyContent="space-between"
              height="100%"
            >
              <Box py={1} display="flex">
                {/* <Box sx={{ height: 12, width: 12 }} pr={3}>
                    <BulletIcon />
                  </Box> */}
                <VSNTypography
                  sx={{
                    fontSize: "15px",
                    fontWeight: 500,
                    color: "#3A3A40",
                    lineHeight: "24px",
                    "& ul": {
                      p: 0,
                    },
                  }}
                  dangerouslySetInnerHTML={{
                    __html:
                      result?.doctor_take_key_insights_from_your_x_ray_card
                        ?.finding,
                  }}
                />
              </Box>

              <Stack direction="row" gap={2}>
                {result?.doctor_take_key_insights_from_your_x_ray_card
                  ?.history && (
                  <Box className={classes.notesListMulti} gap={2}>
                    <HistoryIcon />
                    <VSNTypography component="p">
                      {result?.doctor_take_key_insights_from_your_x_ray_card
                        ?.history?.title ?? ""}
                      <br />{" "}
                      <span>
                        {result?.doctor_take_key_insights_from_your_x_ray_card
                          ?.history?.history ?? ""}
                      </span>
                    </VSNTypography>
                  </Box>
                )}

                {result?.doctor_take_key_insights_from_your_x_ray_card
                  ?.comparisons && (
                  <Box className={classes.notesListMulti} gap={2}>
                    <ComparisonIcon />
                    <VSNTypography component="p">
                      {result?.doctor_take_key_insights_from_your_x_ray_card
                        ?.comparisons?.title ?? ""}
                      <br />{" "}
                      <span>
                        {result?.doctor_take_key_insights_from_your_x_ray_card
                          ?.comparisons?.comparison ?? ""}
                      </span>
                    </VSNTypography>
                  </Box>
                )}
              </Stack>

              <VSNTypography
                sx={{
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "var(--gray)",
                }}
                pt={2}
              >
                {result?.doctor_take_key_insights_from_your_x_ray_card
                  ?.electronically_signed ?? ""}
              </VSNTypography>
            </Stack>
          </Stack>
          <PDFRecommendationSection
            data={result?.be_proactive ?? {}}
            staticData={swiperData}
          />
        </Stack>
      </Box>
    </Box>
  );
});

export default function XRayDashboardResult({
  endPointToCall,
  showBreadCrumbs = true,
  breadCrumbAndButton,
  releaseButton,
  assessmentUserInfo,
  viewAllResult,
  from,
}) {
  const classes = PhysicalOnePagerResultStyle();

  const {
    AssessmentResult: containerResult,
    loader,
    currentAssessment,
  } = from === "viewAll" ? {} : MRFResultContainer({ endPointToCall });

  const AssessmentResult = from === "viewAll" ? viewAllResult : containerResult;

  const [loading, setLoading] = useState(false);
  const printRef = useRef();

  const breadcrumbsRoutes = [
    {
      path: "/assessments",
      label: "Assessments",
    },
    {
      path: `/assessments/${currentAssessment?.assessment_id}`,
      label: `X-ray`,
    },
  ];

  const handleDownloadPDF = () => {
    setLoading(true);
    const element = printRef.current;

    const adminFilename = `${assessmentUserInfo?.name}_${assessmentUserInfo?.assessment_name}.pdf`;
    const userFilename = `${AssessmentResult?.info?.name}_${currentAssessment?.assessment_name}.pdf`;
    const fileName = assessmentUserInfo ? adminFilename : userFilename;

    const options = {
      margin: [2, 6],
      filename: fileName,
      image: { type: "jpeg", quality: 0.7 },
      html2canvas: { scale: 1.7, useCORS: true, allowTaint: true },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    };

    html2pdf()
      .from(element)
      .set(options)
      .toPdf()
      .get("pdf")
      .then((pdf) => {
        pdf.save(fileName);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const PDFDetails = assessmentUserInfo ?? AssessmentResult?.info;

  if (from !== "viewAll" && (loader?.assessmentResult || !AssessmentResult)) {
    return <VSNLoader variant="fullPage" />;
  }

  return (
    <div className={classes.mainLayout}>
      {from !== "viewAll" ? (
        showBreadCrumbs ? (
          <Box mt={2.5} mb={1.8}>
            <VSNBreadcrumbs {...{ breadcrumbsRoutes }} />
          </Box>
        ) : (
          <Box className={classes.headingButton}>
            <Box>{breadCrumbAndButton}</Box>
            <Stack
              direction="row"
              gap={{ sm: 2, xs: 1 }}
              alignItems="center"
              flexWrap="wrap"
              sx={{ width: { xsm: "auto", xs: "100%" } }}
            >
              <VSNButton
                className={classes.PrintButton}
                handleClick={handleDownloadPDF}
                isLoading={loading}
              >
                <Stack direction="row" alignItems="center" gap={1}>
                  <PrintOutlinedIcon />
                  Print X Ray
                </Stack>
              </VSNButton>
              {AssessmentResult?.is_locked ? releaseButton : ""}
            </Stack>
          </Box>
        )
      ) : null}

      {AssessmentResult?.message === "No report found" ? (
        <Stack direction="row" justifyContent="center" p={5}>
          <VSNTypography variant="h5">No Report Found</VSNTypography>
        </Stack>
      ) : (
        <>
          {showBreadCrumbs && (
            <Stack
              direction={{ xsm: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <VSNTypography
                fontSize={{ xsm: 24, sm: 34 }}
                sx={{ fontWeight: 700 }}
              >
                {AssessmentResult?.title ?? ""}
              </VSNTypography>
              <VSNButton
                className={classes.PrintButton}
                handleClick={handleDownloadPDF}
                isLoading={loading}
              >
                <Stack direction="row" alignItems="center" gap={1}>
                  <PrintOutlinedIcon />
                  Print X Ray Report
                </Stack>
              </VSNButton>
            </Stack>
          )}

          <PrintComponent
            ref={printRef}
            result={AssessmentResult}
            header={PDFDetails}
          />

          <Grid container spacing={3}>
            <Grid item md={12} xs={12}>
              <Stack className={classes.assessmentReportCard}>
                <Box sx={{ display: "block" }}>
                  <VSNTypography className={classes.mdHeadingMain}>
                    {AssessmentResult?.clinical_summary_of_your_x_ray_card
                      ?.title ?? ""}
                  </VSNTypography>
                </Box>
                {/* <StarIcon /> */}
                <Stack direction={{ md: "row" }} spacing={2}>
                  <Box flex={1}>
                    <List
                      className={
                        AssessmentResult?.clinical_summary_of_your_x_ray_card
                          ?.content?.length === 1
                          ? classes.notesListOne
                          : classes.notesList
                      }
                    >
                      {AssessmentResult?.clinical_summary_of_your_x_ray_card?.content?.map(
                        (el, index) => (
                          <React.Fragment key={index}>
                            <ListItem>{el?.text}</ListItem>
                          </React.Fragment>
                        )
                      )}
                    </List>
                  </Box>
                </Stack>
              </Stack>
            </Grid>

            <Grid item xl={6} lg={6} xs={12}>
              <Stack className={classes.assessmentReportCard}>
                <VSNTypography
                  variant="h3"
                  component="h3"
                  className={classes.mdHeadingprimary}
                  mb={1}
                >
                  {AssessmentResult
                    ?.doctor_take_key_insights_from_your_x_ray_card?.title ??
                    ""}
                </VSNTypography>
                <Stack
                  direction="column"
                  justifyContent="space-between"
                  height="100%"
                >
                  <Box py={1} display="flex">
                    {/* <Box sx={{ height: 12, width: 12 }} pr={3}>
                  <BulletIcon />
                </Box> */}
                    {/* <Box pr={1} width="80px">
                      <VSNImage
                        src={
                          AssessmentResult
                            ?.doctor_take_key_insights_from_your_x_ray_card
                            ?.icon
                        }
                        alt="listicon"
                      />
                    </Box> */}

                    <VSNTypography
                      sx={{
                        fontSize: "15px",
                        fontWeight: 500,
                        color: "#3A3A40",
                        lineHeight: "24px",
                        "& ul": {
                          p: 0,
                        },
                      }}
                      dangerouslySetInnerHTML={{
                        __html:
                          AssessmentResult
                            ?.doctor_take_key_insights_from_your_x_ray_card
                            ?.finding,
                      }}
                    />
                  </Box>

                  <Box sx={{ display: { sm: "flex", xs: "block" } }} gap={2}>
                    {AssessmentResult
                      ?.doctor_take_key_insights_from_your_x_ray_card
                      ?.history && (
                      <Box className={classes.notesListMulti} gap={2}>
                        <Box>
                          <VSNImage
                            src={
                              AssessmentResult
                                ?.doctor_take_key_insights_from_your_x_ray_card
                                ?.history?.icon
                            }
                            alt="listicon"
                          />
                        </Box>
                        <VSNTypography component="p">
                          {
                            AssessmentResult
                              ?.doctor_take_key_insights_from_your_x_ray_card
                              ?.history?.title
                          }
                          <br />{" "}
                          <span>
                            {
                              AssessmentResult
                                ?.doctor_take_key_insights_from_your_x_ray_card
                                ?.history?.history
                            }
                          </span>
                        </VSNTypography>
                      </Box>
                    )}

                    {AssessmentResult
                      ?.doctor_take_key_insights_from_your_x_ray_card
                      ?.comparisons && (
                      <Box className={classes.notesListMulti} gap={2}>
                        <Box>
                          <VSNImage
                            src={
                              AssessmentResult
                                ?.doctor_take_key_insights_from_your_x_ray_card
                                ?.comparisons?.icon
                            }
                            alt="listicon"
                          />
                        </Box>
                        <VSNTypography component="p">
                          {
                            AssessmentResult
                              ?.doctor_take_key_insights_from_your_x_ray_card
                              ?.comparisons?.title
                          }
                          <br />{" "}
                          <span>
                            {AssessmentResult
                              ?.doctor_take_key_insights_from_your_x_ray_card
                              ?.comparisons?.comparison ?? "None"}
                          </span>
                        </VSNTypography>
                      </Box>
                    )}
                  </Box>

                  <VSNTypography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 500,
                      color: "var(--gray)",
                    }}
                    pt={2}
                  >
                    {AssessmentResult
                      ?.doctor_take_key_insights_from_your_x_ray_card
                      ?.electronically_signed ?? ""}
                  </VSNTypography>
                </Stack>
              </Stack>
            </Grid>

            <Grid item xl={6} lg={6} xs={12}>
              <Stack
                className={classes.assessmentReportCardWhite}
                sx={{ height: "auto" }}
              >
                <VSNTypography
                  variant="h3"
                  component="h3"
                  className={classes.mdHeading}
                  mb={1}
                >
                  {AssessmentResult?.chest_x_ray_card_faqs?.title ?? ""}
                </VSNTypography>
                <VSNTypography component="p" className={classes.desc}>
                  {AssessmentResult?.chest_x_ray_card_faqs?.description ?? ""}
                </VSNTypography>
                <List
                  className={classes.dailyFunctioning}
                  sx={{ maxHeight: "320px" }}
                >
                  {AssessmentResult?.chest_x_ray_card_faqs?.questions?.map(
                    (el, index) => (
                      <ListItem key={index}>
                        <Box mt={0.5}>
                          <PurpalFlowerBullet />
                        </Box>
                        <VSNTypography color="var(--blackGray)">
                          <span style={{ color: "#86650A", fontWeight: 500 }}>
                            {el?.question ?? ""}{" "}
                          </span>
                          <br />
                          {el?.answer}
                        </VSNTypography>
                      </ListItem>
                    )
                  )}
                </List>
              </Stack>
            </Grid>

            <SwiperCardComponent
              title={AssessmentResult?.be_proactive?.title ?? ""}
              description={AssessmentResult?.be_proactive?.description ?? ""}
              data={AssessmentResult?.be_proactive?.recommendations ?? []}
            />
          </Grid>
        </>
      )}
    </div>
  );
}
